// Shoppable landing page targeting "groomsmen gifts" and "groomsmen wallets".
// Bundles are single cart line items, so a whole wedding party pack is one
// add-to-cart, one Stripe line item. Pricing ladder: $5/$7/$10 off per Brik
// as the pack grows (adjust PACKS below to tune).
import React, { useState, useContext, useRef } from "react";
import confetti from "canvas-confetti";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CartAddedPopup from "../components/CartAddedPopup";
import { CartContext } from "../context/CartContext";
import {
  SITE_URL,
  PRODUCT_IMAGE,
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";
import idPhoto from "../assets/newest-version/ID Card Holder.jpg";
import cardsPhoto from "../assets/newest-version/Credit Cards Extended.jpg";
import cashPhoto from "../assets/newest-version/Back - with cash.jpg";
import keysPhoto from "../assets/newest-version/Back - Keys Focus.jpg";
import trackerPhoto from "../assets/newest-version/Integrated Tracking Card.jpg";
import boxesPhoto from "../assets/boxes1.webp";

const futura = "'Futura', sans-serif";

const PACKS = [
  { id: "groomsmen-1", label: "Best Man", count: 1, perBrik: 69.99 },
  { id: "groomsmen-4", label: "Four Groomsmen", count: 4, perBrik: 64.99 },
  { id: "groomsmen-6", label: "Six Groomsmen", count: 6, perBrik: 62.99 },
  { id: "groomsmen-8", label: "Eight Groomsmen", count: 8, perBrik: 59.99 },
];

const packTotal = (pack) => +(pack.count * pack.perBrik).toFixed(2);

const GROOMSMEN_FAQ = [
  {
    q: "What makes The Brik a good groomsmen gift?",
    a: "It is a practical gift your groomsmen can use daily to carry their cards, ID, cash, keys, and tracking card. Unlike a flask or matching socks, it stays in their pocket long after the wedding.",
  },
  {
    q: "Can I order one for every groomsman?",
    a: "Yes. The Brik is available in wedding-party bundles for a best man or groups of four, six, eight, or more. Each pack is one add-to-cart, and per-Brik pricing drops as the pack grows.",
  },
  {
    q: "Can each gift include a different name?",
    a: "Yes. Each Brik can be packaged with an individual name card and a personalized message from the groom. After checkout, reply to your order confirmation email with the names and message you want included.",
  },
  {
    q: "When should I order my groomsmen gifts?",
    a: "We recommend ordering at least three to four weeks before you plan to give them out. Orders are processed in about 1 business day and arrive within 5-7 days, so this leaves comfortable margin.",
  },
  {
    q: "When should I give my groomsmen their gifts?",
    a: "Most couples give groomsmen gifts during the bachelor party, rehearsal dinner, or while getting ready on the wedding day.",
  },
];

const CARRY_POINTS = [
  { img: idPhoto, alt: "ID in the front slot of the metal Brik groomsmen wallet", title: "ID in front" },
  { img: cardsPhoto, alt: "Cards in the middle compartment of the metal Brik groomsmen wallet", title: "Cards in the middle" },
  { img: cashPhoto, alt: "Folded cash in the back of the metal Brik groomsmen wallet", title: "Folded cash in back" },
  { img: keysPhoto, alt: "Keys attached to the side keyring of the metal Brik groomsmen wallet", title: "Keys on the side" },
  { img: trackerPhoto, alt: "Tracking card inside the metal Brik groomsmen wallet", title: "Tracking card inside" },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  border: "none",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  cursor: "pointer",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const GroomsmenGifts = () => {
  const { addToCart } = useContext(CartContext);
  const [selectedPack, setSelectedPack] = useState(PACKS[1]);
  const [platform, setPlatform] = useState(null); // "Apple" | "Android"
  const [notice, setNotice] = useState("");
  const [popupOpen, setPopupOpen] = useState(false);
  const [addedName, setAddedName] = useState("");
  const [addedId, setAddedId] = useState("");
  const packsRef = useRef(null);

  const scrollToPacks = () =>
    packsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleAddPack = (e) => {
    if (!platform) {
      setNotice("Pick Apple or Android first, so each Brik ships with the right tracking card.");
      setTimeout(() => setNotice(""), 3000);
      return;
    }
    const total = packTotal(selectedPack);
    const plural = selectedPack.count === 1 ? "Brik" : "Briks";
    const item = {
      id: `${selectedPack.id}-${platform.toLowerCase()}`,
      name: `Groomsmen Pack: ${selectedPack.count} ${plural} (${platform})`,
      price: total,
      quantity: 1,
      link: "/groomsmen-gifts",
      images: [heroPhoto],
    };
    addToCart(item);
    setAddedName(item.name);
    setAddedId(item.id);
    setPopupOpen(true);
    setNotice("Added to cart!");
    setTimeout(() => setNotice(""), 2000);
    if (typeof window.gtag === "function") {
      window.gtag("event", "add_to_cart", {
        currency: "USD",
        value: total,
        items: [
          {
            item_id: item.id,
            item_name: item.name,
            price: total,
            quantity: 1,
          },
        ],
      });
    }
    const rect = e.currentTarget.getBoundingClientRect();
    confetti({
      particleCount: 100,
      spread: 75,
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight,
      },
    });
  };

  const groomsmenProductJsonLd = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "@id": `${SITE_URL}/groomsmen-gifts#product`,
    name: "The Brik Groomsmen Gift Pack",
    description:
      "Gift-ready metal wallet bundles for wedding parties. Each Brik holds ID, cards, folded cash, keys, and a rechargeable tracking card. Packs for a best man or four, six, or eight groomsmen, with individual name cards and a personalized message.",
    brand: { "@type": "Brand", name: "The Brik" },
    image: PRODUCT_IMAGE,
    sku: "GROOMSMEN-PACK",
    url: `${SITE_URL}/groomsmen-gifts`,
    isRelatedTo: { "@id": `${SITE_URL}/metal-wallet#product` },
    offers: {
      "@type": "AggregateOffer",
      priceCurrency: "USD",
      lowPrice: "69.99",
      highPrice: String(packTotal(PACKS[3])),
      offerCount: PACKS.length,
      availability: "https://schema.org/InStock",
      url: `${SITE_URL}/groomsmen-gifts`,
    },
  };

  const faqJsonLd = makeFaqJsonLd(GROOMSMEN_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Groomsmen Gifts", path: "/groomsmen-gifts" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Groomsmen Gifts They'll Actually Use"
        description="Give your groomsmen a practical metal wallet that holds their ID, cards, cash, keys, and tracker. Shop gift-ready Brik bundles for your wedding party."
        path="/groomsmen-gifts"
        type="product"
        keywords="groomsmen gifts, groomsmen wallets, useful groomsmen gifts, groomsmen gift ideas, wedding party gifts, best man gift, personalized groomsmen gifts"
        jsonLd={[groomsmenProductJsonLd, faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "820px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            GROOMSMEN GIFTS
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(32px, 4.5vw, 54px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            Groomsmen Gifts They'll Actually Use
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "18px",
              lineHeight: 1.55,
              color: "#cfcfcf",
              maxWidth: "640px",
              margin: "0 auto 14px",
            }}
          >
            The best groomsmen gifts are useful long after the wedding is over.
          </p>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "640px",
              margin: "0 auto 14px",
            }}
          >
            Give each member of your wedding party a Brik, the minimalist metal
            wallet that keeps their ID, cards, folded cash, keys, and tracking
            card together in one place.
          </p>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "640px",
              margin: "0 auto 26px",
            }}
          >
            No novelty flask. No matching socks. Just a practical gift your
            guys can carry every day.
          </p>
          <button type="button" onClick={scrollToPacks} style={ctaButton}>
            Build Your Groomsmen Pack
          </button>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "16px 0 0",
              fontWeight: 600,
            }}
          >
            Gift-ready bundles available for wedding parties of all sizes.
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="Metal wallet groomsmen gifts: The Brik with ID, cards, cash, and keys"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Not another flask */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center" }}>
            Not another flask
          </h2>
          <p
            style={{
              ...bodyText,
              textAlign: "center",
              maxWidth: "620px",
              margin: "0 auto 40px",
            }}
          >
            Most groomsmen gifts are designed for the wedding weekend. The Brik
            is designed for every day after it.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
              gap: "18px",
            }}
          >
            {CARRY_POINTS.map((point) => (
              <div key={point.title} style={{ textAlign: "center" }}>
                <div
                  style={{
                    background: "#989898",
                    borderRadius: "12px",
                    overflow: "hidden",
                    aspectRatio: "1 / 1",
                    marginBottom: "12px",
                  }}
                >
                  <img
                    src={point.img}
                    alt={point.alt}
                    loading="lazy"
                    width={1024}
                    height={1024}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    margin: 0,
                    color: "#111",
                    letterSpacing: "-0.2px",
                  }}
                >
                  {point.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bundle picker */}
      <section
        ref={packsRef}
        style={{ background: "#f7f7f7", padding: "70px 24px" }}
      >
        <div style={{ maxWidth: "860px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center" }}>
            Build your groomsmen pack
          </h2>
          <p
            style={{
              ...bodyText,
              textAlign: "center",
              maxWidth: "560px",
              margin: "0 auto 36px",
            }}
          >
            One click adds the whole wedding party to your cart. Per-Brik
            pricing drops as your party grows.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: "16px",
              marginBottom: "28px",
            }}
          >
            {PACKS.map((pack) => {
              const selected = selectedPack.id === pack.id;
              return (
                <button
                  key={pack.id}
                  type="button"
                  onClick={() => setSelectedPack(pack)}
                  style={{
                    background: selected ? "#0c0c0c" : "white",
                    color: selected ? "white" : "#111",
                    border: selected ? "2px solid #ff7d1a" : "2px solid #ddd",
                    borderRadius: "14px",
                    padding: "22px 16px",
                    cursor: "pointer",
                    fontFamily: futura,
                    textAlign: "center",
                    transition: "all 0.15s ease",
                  }}
                >
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      color: selected ? "#ff7d1a" : "#888",
                      marginBottom: "6px",
                    }}
                  >
                    {pack.label}
                  </div>
                  <div style={{ fontSize: "26px", fontWeight: 700, letterSpacing: "-0.5px" }}>
                    ${packTotal(pack).toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontSize: "12.5px",
                      color: selected ? "#bbb" : "#777",
                      marginTop: "4px",
                    }}
                  >
                    ${pack.perBrik.toFixed(2)} per Brik
                    {pack.count > 1 ? ` · ${pack.count} Briks` : ""}
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ maxWidth: "480px", margin: "0 auto", textAlign: "center" }}>
            <p
              style={{
                fontFamily: futura,
                fontSize: "13px",
                fontWeight: 700,
                letterSpacing: "0.5px",
                color: "#111",
                margin: "0 0 4px",
                textTransform: "uppercase",
              }}
            >
              Which phones do your groomsmen use?
            </p>
            <p
              style={{
                fontFamily: futura,
                fontSize: "12px",
                color: "#777",
                margin: "0 0 12px",
              }}
            >
              So each Brik ships with the right tracking card. Mixed party?
              Add an Apple pack and an Android pack.
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "center", marginBottom: "18px" }}>
              {["Apple", "Android"].map((p) => {
                const selected = platform === p;
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(p)}
                    style={{
                      flex: "1 1 140px",
                      maxWidth: "180px",
                      padding: "14px 20px",
                      borderRadius: "8px",
                      border: selected ? "2px solid #ff7d1a" : "2px solid #ccc",
                      background: selected ? "#fff3e9" : "white",
                      color: selected ? "#ff7d1a" : "#333",
                      fontWeight: 700,
                      fontSize: "15px",
                      cursor: "pointer",
                      letterSpacing: "0.5px",
                      fontFamily: futura,
                      transition: "all 0.15s ease",
                    }}
                  >
                    {selected ? "✓ " : ""}
                    {p}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={handleAddPack}
              style={{ ...ctaButton, width: "100%" }}
            >
              Add {selectedPack.label} pack · ${packTotal(selectedPack).toFixed(2)}
            </button>
            {notice && (
              <p
                style={{
                  fontFamily: futura,
                  color: notice.startsWith("Added") ? "#2e7d32" : "#b71c1c",
                  fontWeight: 600,
                  fontSize: "14px",
                  margin: "12px 0 0",
                }}
              >
                {notice}
              </p>
            )}
            <p
              style={{
                fontFamily: futura,
                fontSize: "12.5px",
                color: "#888",
                margin: "14px 0 0",
                lineHeight: 1.5,
              }}
            >
              Free US shipping on every order · processed in about 1
              business day, arrives in 5-7 days · 30-day exchange guarantee.
              Full specs on the{" "}
              <a href="/metal-wallet" style={{ color: "#ff7d1a", fontWeight: 600 }}>
                Metal Brik product page
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Personalization */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "36px",
            alignItems: "center",
          }}
        >
          <div>
            <img
              src={boxesPhoto}
              alt="Gift-ready Brik boxes packed for a wedding party"
              loading="lazy"
              style={{ width: "100%", borderRadius: "16px", display: "block" }}
            />
          </div>
          <div>
            <h2 style={sectionHeading}>Gift-ready, without the wait</h2>
            <p style={bodyText}>
              Every groomsmen pack ships gift-ready: individual boxes, a name
              card for each groomsman, an optional personal message from the
              groom, and your wedding date printed on the insert.
            </p>
            <p style={bodyText}>
              After checkout, reply to your order confirmation email with the
              names and message you want included, and we'll handle the rest.
            </p>
            <p style={{ ...bodyText, fontSize: "14px", color: "#666", margin: 0 }}>
              Want engraved initials or a custom design on the wallets
              themselves? That's a{" "}
              <a href="/bulk" style={{ color: "#ff7d1a", fontWeight: 600 }}>
                bulk custom order
              </a>
              , free mockup included.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Groomsmen gift questions, answered
          </h2>
          {GROOMSMEN_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "white",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            Your boys showed up for you.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            Don't give them matching socks. Give them a wallet they'll carry
            every day after the wedding.
          </p>
          <button type="button" onClick={scrollToPacks} style={ctaButton}>
            Build Your Groomsmen Pack
          </button>
        </div>
      </section>

      <Footer />

      <CartAddedPopup
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        productId={addedId}
        productName={addedName}
      />
    </div>
  );
};

export default GroomsmenGifts;
